import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogOut, Send, LifeBuoy } from 'lucide-react';
import { api, day } from '../api';
import { useCivrAuth } from '../AuthContext';
import { PageHeader, Field } from '../ui';
import { LANGS, useLang } from '../i18n';
import { useVoices, VoiceSelect } from '../VoiceMaker';
import { cp } from '../paths';
import { useShell } from '../Shell';
import { LockedNote } from '../Upgrade';
import { Security } from './settings/Security';
import { Referral, Suggest } from './settings/Rewards';
import { Webhooks } from './settings/Webhooks';
import { InstallApp } from './settings/InstallApp';
import { EmailPrefs } from './settings/EmailPrefs';
import { useUnsaved, SaveRow } from '../guard';

function Profile({ account, setAccount }) {
  const [name, setName] = useState(account.name || '');
  const [busy, setBusy] = useState(false);
  const u = useUnsaved(name);
  const save = async (e) => { e.preventDefault(); setBusy(true); try { const d = await api('/auth/profile', { method: 'PATCH', body: { name } }); setAccount(d.account); u.reset(); toast.success('Profile saved'); } catch (err) { toast.error(err.message); } finally { setBusy(false); } };
  return (
    <form onSubmit={save} className="civr-card p-6 space-y-6" data-testid="settings-profile-form" {...u.bind}>
      <div className="font-display text-2xl font-bold">Profile</div>
      <Field label="Email"><input className="civr-input" value={account.email} disabled data-testid="settings-email" /></Field>
      <Field label="Name"><input className="civr-input" value={name} onChange={e => setName(e.target.value)} data-testid="settings-name-input" /></Field>
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="civr-pill" data-testid="settings-member-since">member since {day(account.createdAt)}</span>
        {account.google && <span className="civr-pill civr-pill-on" data-testid="settings-google-linked">Google linked</span>}
        {account.hasPassword && <span className="civr-pill" data-testid="settings-password-set">password set</span>}
      </div>
      <SaveRow dirty={u.dirty} testid="settings-profile-unsaved-pill"><button className="civr-btn civr-btn-primary" disabled={busy} data-testid="settings-profile-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save profile</button></SaveRow>
    </form>
  );
}

function Password({ account, refresh }) {
  const [f, setF] = useState({ currentPassword: '', newPassword: '' });
  const [busy, setBusy] = useState(false);
  const u = useUnsaved(f);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api('/auth/change-password', { method: 'POST', body: f }); toast.success('Password updated — other sessions were signed out'); setF({ currentPassword: '', newPassword: '' }); u.reset(); refresh(); } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="civr-card p-6 space-y-6" data-testid="settings-password-form" {...u.bind}>
      <div className="font-display text-2xl font-bold">{account.hasPassword ? 'Change password' : 'Set a password'}</div>
      {account.hasPassword && <Field label="Current password"><input type="password" required className="civr-input" value={f.currentPassword} onChange={e => setF({ ...f, currentPassword: e.target.value })} data-testid="settings-current-password-input" /></Field>}
      <Field label="New password" hint="At least 8 characters."><input type="password" required minLength={8} className="civr-input" value={f.newPassword} onChange={e => setF({ ...f, newPassword: e.target.value })} data-testid="settings-new-password-input" /></Field>
      <SaveRow dirty={u.dirty} testid="settings-password-unsaved-pill"><button className="civr-btn civr-btn-primary" disabled={busy} data-testid="settings-password-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save password</button></SaveRow>
    </form>
  );
}

function Language() {
  const { lang, setLang } = useLang();
  return (
    <div className="civr-card p-6 space-y-4" data-testid="settings-language-card">
      <div className="font-display text-2xl font-bold">Language</div>
      <p className="text-sm text-[#a1a1aa]">The whole app, including the home page. We start with your browser’s language.</p>
      <div className="flex flex-wrap gap-2" data-no-translate>{LANGS.map(([c, name]) => <button key={c} type="button" onClick={() => setLang(c)} aria-pressed={lang === c} className={`civr-pill cursor-pointer !normal-case !tracking-normal !text-sm h-10 ${lang === c ? 'civr-pill-on' : ''}`} data-testid={`settings-lang-${c}`}>{name}</button>)}</div>
    </div>
  );
}

function DefaultVoice() {
  const { voices, speeds, def, canSave } = useVoices();
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const cur = v || def;
  const save = async () => { setBusy(true); try { await api('/prefs/voice', { method: 'PUT', body: cur }); toast.success('Default voice saved'); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  return (
    <div className="civr-card p-6 space-y-4" data-testid="settings-voice-card">
      <div className="font-display text-2xl font-bold">Default AI voice</div>
      <p className="text-sm text-[#a1a1aa]">Used for every new script and AI voice message so your calls always sound the same.</p>
      {canSave ? <>
        <div className="flex flex-wrap gap-2"><VoiceSelect value={cur.voice} onChange={(x) => setV({ ...cur, voice: x })} voices={voices} testid="settings-voice-select" />
          <select className="civr-input !w-24" value={cur.speed} onChange={e => setV({ ...cur, speed: Number(e.target.value) })} data-testid="settings-speed-select" aria-label="Speed">{speeds.map(s => <option key={s} value={s}>{`${s}×`}</option>)}</select></div>
        <button className="civr-btn civr-btn-primary" disabled={busy} onClick={save} data-testid="settings-voice-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save voice</button>
      </> : <div className="space-y-3" data-testid="settings-voice-locked">
        <div className="flex flex-wrap gap-2 opacity-50 pointer-events-none" aria-hidden="true"><VoiceSelect value={cur.voice} onChange={() => {}} voices={voices} testid="settings-voice-select-locked" /></div>
        <LockedNote text="Keep one voice for every call and message on the Business plan." feature="A saved default voice" testid="settings-voice-lock" />
      </div>}
    </div>
  );
}

function Community() {
  const { catalog } = useShell();
  if (!catalog?.channelLink && !catalog?.supportLink) return null;
  return (
    <div className="civr-card p-6 space-y-4" data-testid="settings-community">
      <div className="font-display text-2xl font-bold">Community & help</div>
      <p className="text-sm text-[#a1a1aa]">News, new countries and daily coupons on our Telegram channel. Questions? Our team answers on Telegram.</p>
      <div className="flex flex-wrap gap-2">
        {catalog.channelLink && <a href={catalog.channelLink} target="_blank" rel="noopener noreferrer" className="civr-btn civr-btn-ghost civr-btn-sm" data-testid="settings-channel-link"><Send className="w-4 h-4" />Telegram channel</a>}
        {catalog.supportLink && <a href={catalog.supportLink} target="_blank" rel="noopener noreferrer" className="civr-btn civr-btn-ghost civr-btn-sm" data-testid="settings-support-link"><LifeBuoy className="w-4 h-4" />Message support</a>}
      </div>
    </div>
  );
}

// /app/settings#webhooks or #install (links from "What's new") scroll to that card — and keep it in place while the cards
// above it finish loading (the page grows under it), until it stops moving or the user scrolls.
function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const id = `settings-${hash.slice(1)}`; // only the loaded card has this id (not its loading placeholder)
    let lastTop = null, lastH = null, still = 0, ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      const el = document.getElementById(id);
      const h = document.documentElement.scrollHeight;
      if (el) {
        const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
        const busy = document.querySelector('[data-testid="settings-page"] [aria-busy="true"]');
        if (top !== lastTop || h !== lastH) { lastTop = top; lastH = h; still = 0; el.scrollIntoView({ block: 'start' }); } else if (!busy) still += 1;
      }
      if (still >= 10 || ticks > 60) clearInterval(t); // settled for ~1.5 s with nothing loading, or 9 s cap
    }, 150);
    const stop = () => clearInterval(t);
    const opts = { passive: true };
    window.addEventListener('wheel', stop, opts);
    window.addEventListener('touchstart', stop, opts);
    window.addEventListener('keydown', stop);
    return () => { clearInterval(t); window.removeEventListener('wheel', stop, opts); window.removeEventListener('touchstart', stop, opts); window.removeEventListener('keydown', stop); };
  }, [hash]);
}

export default function Settings() {
  const { account, setAccount, refresh, logout } = useCivrAuth();
  const nav = useNavigate();
  useHashScroll();
  return (
    <div data-testid="settings-page">
      <PageHeader label="Account" title="Settings" sub="This web account is separate from any Telegram bot account." testid="settings-header">
        <button className="civr-btn civr-btn-ghost civr-btn-sm" onClick={async () => { nav(cp(), { replace: true }); await logout(); }} data-testid="settings-logout-button"><LogOut className="w-4 h-4" />Sign out</button>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-4 max-w-5xl"><Profile account={account} setAccount={setAccount} /><Password account={account} refresh={refresh} /><Security account={account} refresh={refresh} /><Referral /><Suggest admin={account.admin} /><Language /><DefaultVoice /><EmailPrefs /><InstallApp /><Community /><Webhooks /></div>
    </div>
  );
}
